import {AdminSelectContainerContext, styles as adminSelectStyles} from "components/admin/select-container"
import React, {useContext, useEffect, useMemo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import {ChalkyLayoutInputContainerContext} from "components/chalky-layout/input-container"
import {styles as chalkyLayoutSelectStyles} from "components/chalky-layout/select-container"
import HayaSelect from "haya-select/build/select"
import {incorporate} from "incorporator"
import {InputContainerContext} from "components/inputs/input-container"
import memo from "set-state-compare/build/memo"
import {useForm} from "@kaspernj/api-maker/build/form"
import useInput from "@kaspernj/api-maker/build/use-input"
import {useModals} from "components/modal"

/**
 * @typedef {import("react").SyntheticEvent} SyntheticEvent
 * @typedef {import("haya-select/build/select").HayaSelectOnChangePayload} HayaSelectOnChangePayload
 * @typedef {import("haya-select/build/select").HayaSelectOptionsResult} HayaSelectOptionsResult
 * @typedef {import("haya-select/build/select").HayaSelectOption} HayaSelectOption
 * @typedef {import("haya-select/build/select").HayaSelectProps} HayaSelectProps
 * @typedef {import("haya-select/build/select").HayaSelectState} HayaSelectState
 * @typedef {import("@kaspernj/api-maker/build/form").FormInputs} FormInputs
 * @typedef {import("@kaspernj/api-maker/build/use-input").InputRefLike} InputRefLike
 * @typedef {import("@kaspernj/api-maker/build/use-input").InputValue} InputValue
 */

/**
 * @typedef {string | number | undefined} WoofHayaSelectStyleValue
 * @typedef {Record<string, WoofHayaSelectStyleValue>} WoofHayaSelectStyleBlock
 */

/**
 * @typedef {object} WoofHayaSelectStyleContext
 * @property {boolean} opened
 * @property {"above" | "below" | "sheet"} [optionsPlacement]
 * @property {HayaSelectState} state
 * @property {WoofHayaSelectStyleBlock} style
 */

/**
 * @typedef {(context: WoofHayaSelectStyleContext) => WoofHayaSelectStyleBlock | undefined} WoofHayaSelectStyleCallback
 * @typedef {Record<string, WoofHayaSelectStyleBlock | WoofHayaSelectStyleCallback | undefined>} WoofHayaSelectStyles
 */

/**
 * @typedef {Record<string, InputValue | InputRefLike | Array<InputValue> | undefined> & {
 *   defaultValue?: InputValue,
 *   defaultValues?: Array<InputValue>,
 *   name?: string,
 *   values?: Array<InputValue>
 * }} WoofUseInputProps
 */

/**
 * @typedef {Partial<HayaSelectProps> & {
 *   name?: string,
 *   optionsAbsolute?: boolean
 * }} WoofRestProps
 */

/**
 * @typedef {object} WoofHayaSelectProps
 * @property {string} [id]
 * @property {boolean} [multiple]
 * @property {string} [name]
 * @property {(...args: Array<SyntheticEvent | undefined>) => void} [onBlur]
 * @property {(payload: HayaSelectOnChangePayload) => void} [onChange]
 * @property {(...args: Array<SyntheticEvent | undefined>) => void} [onFocus]
 * @property {Array<HayaSelectOption> | (() => Array<HayaSelectOption> | HayaSelectOptionsResult)} options
 * @property {boolean} [optionsAbsolute]
 * @property {WoofHayaSelectStyles} [styles]
 * @property {boolean} [woofInputStyling]
 */

/**
 * @typedef {object} WoofHayaSelectState
 * @property {boolean} focus
 * @property {boolean} valuePresent
 */

/**
 * @extends {ShapeComponent<WoofHayaSelectProps, WoofHayaSelectState>}
 */
class WoofHayaSelect extends ShapeComponent {
  /** @type {WoofHayaSelectState} */
  state = {
    focus: false,
    valuePresent: false
  }

  /** @type {FormInputs | null} */
  form = null

  /** @type {WoofUseInputProps} */
  inputProps = {}

  /** @type {ReturnType<typeof useModals>} */
  modals = {active: undefined, modals: {}}

  /** @type {WoofRestProps} */
  restProps = {}

  setup() {
    const {inputProps, restProps} = useInput({props: this.props, wrapperOptions: {type: "select"}})
    const woofInputProps = /** @type {WoofUseInputProps} */ (inputProps)
    const woofRestProps = /** @type {WoofRestProps} */ (restProps)
    const defaultValue = woofInputProps.defaultValue
    const defaultValues = woofInputProps.defaultValues

    this.form = useForm()
    this.modals = useModals()
    this.inputProps = woofInputProps
    this.restProps = woofRestProps

    if (this.state.valuePresent === false && this.tt.valuePresentFromInputProps(woofInputProps)) {
      this.state.valuePresent = true
    }

    useMemo(() => {
      const {form} = this.tt

      if (!form || !woofInputProps.name) return

      if (typeof defaultValue != "undefined") {
        form.setValue(woofInputProps.name, defaultValue)
      } else if (Array.isArray(defaultValues) && defaultValues.length > 0) {
        form.setValue(woofInputProps.name, this.props.multiple ? defaultValues : defaultValues[0])
      }
    }, [])

    useEffect(() => () => {
      const {form} = this.tt

      if (form && woofInputProps.name) {
        form.unsetValue(woofInputProps.name)
      }
    }, [this.form, woofInputProps.name])
  }

  /**
   * @param {{defaultValues?: Array<InputValue>, values?: Array<InputValue>}} inputProps
   * @returns {boolean}
   */
  valuePresentFromInputProps = (inputProps) => {
    if (inputProps?.defaultValues?.length > 0 || inputProps?.values?.length > 0) {
      return true
    }

    return false
  }

  render() {
    const adminSelectContainerContext = useContext(AdminSelectContainerContext)
    const chalkyLayoutInputContainerContext = useContext(ChalkyLayoutInputContainerContext)
    const inputContainerContext = useContext(InputContainerContext)
    const {restProps: restPropsFromUseInput} = this.tt
    const {styles = {}, woofInputStyling} = this.props
    const {
      containerProps: _containerProps,
      hint: _hint,
      label: _label,
      onBlur: _onBlur,
      onChange: _onChange,
      onFocus: _onFocus,
      optionsAbsolute: optionsAbsoluteFromProps,
      ...restProps
    } = restPropsFromUseInput

    if (adminSelectContainerContext == "withinAdminSelectContainer") {
      incorporate(styles, adminSelectStyles)
    }

    if (chalkyLayoutInputContainerContext == "withinChalkyLayoutInputContainer") {
      incorporate(styles, chalkyLayoutSelectStyles)
      styles.selectContainer ||= {}

      if (this.s.focus) {
        styles.selectContainer.backgroundColor = "#fff"
        styles.selectContainer.border = "1px solid #fff"
        styles.selectContainer.boxShadow = "0px 14px 40px 0px rgba(44, 44, 150, 0.2)"
      }

      if (!this.s.focus && this.s.valuePresent) {
        styles.selectContainer.border = "1px solid #d6e2f9"
      }
    }

    styles.selectContainer ||= {}
    const selectContainerStyleActual = Object.assign({}, styles.selectContainer)
    const hiddenInputName = restProps.name || this.props.id

    if (inputContainerContext == "withinInputContainer") {
      selectContainerStyleActual.paddingTop = 17
      selectContainerStyleActual.paddingRight = 26
      selectContainerStyleActual.paddingBottom = 17
      selectContainerStyleActual.paddingLeft = 26
      selectContainerStyleActual.border = 0
      selectContainerStyleActual.background = "transparent"
    }

    styles.selectContainer = selectContainerStyleActual

    if (woofInputStyling) {
      styles.optionPresentationText ||= {}

      styles.optionPresentationText.color = "#666"
      styles.optionPresentationText.fontSize ||= 17

      styles.currentSelected ||= {}

      styles.currentSelected.overflow = "hidden"
      styles.currentSelected.whiteSpace = "nowrap"

      styles.optionsContainer ||= {}

      styles.optionsContainer.border = "1px solid #dcdcdc"

      selectContainerStyleActual.paddingTop = 17
      selectContainerStyleActual.paddingRight = 26
      selectContainerStyleActual.paddingBottom = 17
      selectContainerStyleActual.paddingLeft = 26
      selectContainerStyleActual.border = "1px solid #dcdcdc"
      selectContainerStyleActual.borderRadius = 10

      styles.chevronContainer ||= {}

      styles.chevronContainer.marginRight = undefined
    }

    const optionsAbsolute = this.tt.modals.active && typeof optionsAbsoluteFromProps == "undefined"
      ? true
      : optionsAbsoluteFromProps
    const optionsPortal = restProps.optionsPortal ?? true
    const hayaSelectStyles = this.tt.stylesForHayaSelect({
      modalOptionsAbsolute: Boolean(this.tt.modals.active && optionsAbsolute && optionsPortal),
      styles
    })

    return (
      <HayaSelect
        onBlur={this.tt.onBlur}
        onChange={this.tt.onChange}
        onFocus={this.tt.onFocus}
        styles={hayaSelectStyles}
        {...restProps}
        name={hiddenInputName}
        optionsAbsolute={optionsAbsolute}
      />
    )
  }

  /**
   * @param {...(SyntheticEvent | undefined)} args
   */
  onBlur = (...args) => {
    if (this.props.onBlur) {
      this.p.onBlur(...args)
    }

    this.s.focus = false
  }

  /**
   * @param {HayaSelectOnChangePayload} payload
   */
  onChange = ({options, ...restProps}) => {
    const {form} = this.tt

    if (this.props.name && form) {
      const values = options.map((option) => option.value)
      let value

      if (this.props.multiple) {
        value = values
      } else {
        value = values[0]
      }

      form.setValue(this.props.name, value)
    }

    if (this.props.onChange) {
      this.p.onChange({options, ...restProps})
    }

    this.s.valuePresent = options.length > 0
  }

  /**
   * @param {...(SyntheticEvent | undefined)} args
   */
  onFocus = (...args) => {
    if (this.props.onFocus) {
      this.p.onFocus(...args)
    }

    this.s.focus = true
  }

  /**
   * @param {{modalOptionsAbsolute: boolean, styles: WoofHayaSelectStyles}} args
   * @returns {WoofHayaSelectStyles}
   */
  stylesForHayaSelect = ({modalOptionsAbsolute, styles}) => {
    if (!modalOptionsAbsolute) return styles

    return Object.assign({}, styles, {
      optionsContainer: this.tt.modalOptionsContainerStyle(styles.optionsContainer)
    })
  }

  /**
   * @param {WoofHayaSelectStyleBlock | WoofHayaSelectStyleCallback | undefined} originalOptionsContainerStyle
   * @returns {WoofHayaSelectStyleCallback}
   */
  modalOptionsContainerStyle = (originalOptionsContainerStyle) => (context) => {
    const customStyle = typeof originalOptionsContainerStyle == "function"
      ? originalOptionsContainerStyle(context)
      : originalOptionsContainerStyle

    let adjustedStyle = customStyle ? Object.assign({}, customStyle) : undefined

    if (context.optionsPlacement == "sheet" || typeof document == "undefined") {
      return adjustedStyle
    }

    const documentElement = document.documentElement
    const scrollTop = documentElement.scrollTop
    const scrollLeft = documentElement.scrollLeft

    if (typeof context.style.top == "number" && scrollTop && typeof adjustedStyle?.top == "undefined") {
      adjustedStyle ||= {}
      adjustedStyle.top = context.style.top - scrollTop
    }

    if (typeof context.style.left == "number" && scrollLeft && typeof adjustedStyle?.left == "undefined") {
      adjustedStyle ||= {}
      adjustedStyle.left = context.style.left - scrollLeft
    }

    return adjustedStyle
  }
}

export default memo(shapeComponent(WoofHayaSelect))
